/**
 * Theme utilities for managing UI layout and mode selection.
 * Layout controls the shell structure (top bar vs sidebar).
 * Mode controls the visual theme (classic, light, dark).
 */

import { getItem, setItem } from './localStorage';
export const VALID_LAYOUTS = ['top', 'sidebar'];
export const VALID_MODES = ['classic', 'light', 'dark'];
export const LAYOUT_STORAGE_KEY = 'truqc-ui-layout';
export const MODE_STORAGE_KEY = 'truqc-ui-mode';
export const DEFAULT_LAYOUT = 'top';
export const DEFAULT_MODE = 'classic';

/**
 * Validate if a layout value is valid.
 */
export const isValidLayout = layout => VALID_LAYOUTS.includes(layout);

/**
 * Validate if a mode value is valid.
 */
export const isValidMode = mode => VALID_MODES.includes(mode);

/**
 * Validate if a mode is valid for the given layout.
 */
export const isValidModeForLayout = (mode, layout) => isValidMode(mode) && isValidLayout(layout);

/**
 * @deprecated Layout is no longer coerced by mode; classic is allowed in sidebar.
 */
export const coerceLayoutForMode = (mode, layout) => layout;

/**
 * @deprecated Mode is no longer coerced by layout; returns mode unchanged.
 */
export const coerceMode = (layout, mode) => mode;

/**
 * Get the current layout from localStorage.
 */
export const getLayout = (defaultValue = DEFAULT_LAYOUT) => {
  const stored = getItem(LAYOUT_STORAGE_KEY, defaultValue);
  return isValidLayout(stored) ? stored : defaultValue;
};

/**
 * Set the layout in localStorage.
 * @returns {boolean} True if successful, false otherwise.
 */
export const setLayout = layout => {
  if (!isValidLayout(layout)) {
    return false;
  }
  return setItem(LAYOUT_STORAGE_KEY, layout);
};

/**
 * Get the current mode from localStorage.
 */
export const getMode = (defaultValue = DEFAULT_MODE) => {
  const stored = getItem(MODE_STORAGE_KEY, defaultValue);
  return isValidMode(stored) ? stored : defaultValue;
};

/**
 * Set the mode in localStorage.
 * @returns {boolean} True if successful, false otherwise.
 */
export const setMode = (mode, currentLayout) => {
  if (!isValidMode(mode)) {
    return false;
  }
  if (currentLayout !== undefined && !isValidModeForLayout(mode, currentLayout)) {
    return false;
  }
  return setItem(MODE_STORAGE_KEY, mode);
};

/**
 * Apply layout and mode as data attributes on document.body.
 * Theme SCSS targets body[data-layout] and body[data-mode]; no theme-* classes.
 */
export const applyLayoutAndModeToBody = (layout, mode) => {
  if (typeof document === 'undefined' || !document.body) {
    return;
  }
  const {
    body
  } = document;
  body.dataset.layout = layout;
  body.dataset.mode = mode;
};